
import React from 'react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Search, Calendar, MessageSquare, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Define the step type for the process
interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

// Define the testimonial type
interface Testimonial {
  id: number;
  quote: string;
  name: string;
  role: string;
  school: string;
}

const HowItWorks = () => {
  const navigate = useNavigate();

  // Process steps
  const steps: Step[] = [
    {
      id: 1,
      title: "Browse Alumni Profiles",
      description: "Explore our diverse network of alumni from top institutions. Filter by school, expertise, industry, and more to find the perfect mentor for your needs.",
      icon: <Search className="h-10 w-10 text-crimson" />
    },
    {
      id: 2,
      title: "Book a Service",
      description: "Select the service that best fits your needs—whether it's a Q&A session, resume review, essay feedback, or specialized preparation. Choose a time that works for your schedule.",
      icon: <Calendar className="h-10 w-10 text-crimson" />
    },
    {
      id: 3,
      title: "Connect and Learn",
      description: "Meet with your alumni mentor via our secure platform. Ask questions, get personalized advice, and gain insider knowledge to help you achieve your academic and career goals.",
      icon: <MessageSquare className="h-10 w-10 text-crimson" />
    },
    {
      id: 4,
      title: "Implement and Succeed",
      description: "Apply the insights and recommendations from your mentor. Follow up with additional sessions as needed to refine your approach and track your progress.",
      icon: <Star className="h-10 w-10 text-crimson" />
    }
  ];

  // Success stories/testimonials
  const testimonials: Testimonial[] = [
    {
      id: 1,
      quote: "Working with my alumni mentor completely transformed my application strategy. Their insights into Harvard's culture and values helped me craft essays that truly reflected who I am while speaking to what the admissions committee was looking for. I'm convinced this guidance was a key factor in my acceptance.",
      name: "Sarah Johnson",
      role: "Incoming Harvard Student",
      school: "Class of 2023"
    },
    {
      id: 2,
      quote: "The resume review service was incredible. My mentor had worked at my dream company for years and knew exactly what recruiters look for. After implementing their suggestions, I started getting interview calls within weeks—including from companies that had previously rejected me.",
      name: "Michael Chen",
      role: "Finance Analyst",
      school: "Stanford University Alumni"
    },
    {
      id: 3,
      quote: "The case prep sessions were rigorous but exactly what I needed. My mentor challenged me with cases even harder than what I faced in my actual interviews. When I walked into BCG's office, I felt confident and prepared. Three weeks later, I received my offer letter.",
      name: "Priya Patel",
      role: "Management Consultant",
      school: "Wharton Alumni"
    }
  ];

  // FAQs
  const faqs = [
    {
      question: "How are alumni mentors selected?",
      answer: "Our alumni mentors go through a thorough screening process. We verify their academic credentials, professional experience, and commitment to mentoring. We also collect feedback after each session to ensure they're providing valuable guidance."
    },
    {
      question: "How much do services cost?",
      answer: "Service fees vary based on the mentor's experience level and the type of service. Q&A sessions typically range from $30-$100 per 30 minutes, resume reviews from $45-$120, and essay reviews from $50-$150. Our goal is to provide options for different budget levels."
    },
    {
      question: "Can I choose a specific alumni mentor?",
      answer: "Yes! You can browse profiles and select the exact mentor you'd like to work with based on their background, expertise, and availability."
    },
    {
      question: "How do I prepare for my session?",
      answer: "After booking, you'll receive preparation guidelines for your specific service. Generally, we recommend coming prepared with specific questions or goals, and sharing any relevant materials in advance so your mentor can review them."
    },
    {
      question: "What if I'm not satisfied with my session?",
      answer: "We stand by the quality of our services. If you're not satisfied, please contact our support team within 48 hours of your session, and we'll work with you to make it right—whether that's scheduling a follow-up or providing a refund."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <NavBar />
      <main className="flex-grow">
        
        {/* Hero Section */}
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl font-bold text-gray-900 font-playfair mb-6">
                How Alumni Insights Works
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                We connect students with alumni who've walked the path you aspire to follow, 
                providing personalized guidance every step of the way.
              </p>
              <Button 
                className="bg-crimson hover:bg-crimson-dark text-white px-8 py-2"
                onClick={() => navigate('/browse')}
              >
                Find Your Mentor
              </Button>
            </div>
          </div>
        </section>
        
        {/* The Process Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 font-playfair mb-12">
              Our Simple Four-Step Process
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((step) => (
                <div key={step.id} className="flex flex-col items-center text-center">
                  <div className="mb-4 p-4 bg-gray-50 rounded-full shadow-sm">
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-gray-600 italic mb-6">
                "Alumni Insights isn't just about getting into your dream school—it's about learning from someone 
                who's been there, understands the challenges, and can help you navigate your unique path to success."
              </p>
              <Button 
                variant="outline" 
                className="border-crimson text-crimson hover:bg-crimson/10"
                onClick={() => navigate('/services')}
              >
                Explore Our Services
              </Button>
            </div>
          </div>
        </section>
        
        {/* Success Stories */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 font-playfair mb-12">
              Success Stories
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="bg-white border border-gray-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center mb-4">
                      <Star className="h-5 w-5 text-yellow-500 fill-current" />
                      <Star className="h-5 w-5 text-yellow-500 fill-current" />
                      <Star className="h-5 w-5 text-yellow-500 fill-current" />
                      <Star className="h-5 w-5 text-yellow-500 fill-current" />
                      <Star className="h-5 w-5 text-yellow-500 fill-current" />
                    </div>
                    <p className="text-gray-600 italic mb-6">"{testimonial.quote}"</p>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                      <p className="text-sm text-gray-500">{testimonial.school}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 font-playfair mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="max-w-3xl mx-auto">
              {faqs.map((faq, index) => (
                <div key={index} className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-lg text-gray-700 mb-6">
                Still have questions? We're here to help!
              </p>
              <Button 
                className="bg-crimson hover:bg-crimson-dark text-white"
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </Button>
            </div>
          </div>
        </section>
        
      </main>
      <Footer />
    </div>
  );
};

export default HowItWorks;
